/**
 * Token Usage Dashboard data hooks
 *
 * @docs-internal
 * @module superadmin/useTokenUsageData
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';

// ── Types ────────────────────────────────────────────────────────────────────

export type TokenUsageSummary = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  runs: number;
  costCents: number;
};

export type TokenUsageByModel = TokenUsageSummary & {
  modelId: string | null;
};

export type TokenUsageTimeSeriesPoint = {
  date: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  runs: number;
  costCents: number;
};

export type TokenUsageCostByModelSeries = {
  modelIds: string[];
  points: Array<{ date: string; [modelId: string]: string | number }>;
};

export type TokenUsageBreakdownRow = {
  groupKey: string;
  groupLabel: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  runs: number;
  costCents: number;
};

export type JobType = 'workflow' | 'pagetest' | 'adhoc' | 'chaos' | 'crawl' | 'other';

export type TokenUsageJobTypeCell = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  runs: number;
  costCents: number;
};

export type TokenUsageJobTypeOrgRow = {
  orgId: string;
  orgName: string | null;
  agenticRuns: number;
  totals: TokenUsageJobTypeCell;
  byJobType: Record<JobType, TokenUsageJobTypeCell>;
};

export type TokenUsageReportTotals = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  runs: number;
  costCents: number;
};

export type TokenUsageReportOrgRow = TokenUsageReportTotals & {
  orgId: string;
  orgName: string | null;
  baselineDailyCostCents: number;
  baselineDailyTokens: number;
  costDeltaCents: number;
  tokenDelta: number;
  costChangePct: number | null;
  tokenChangePct: number | null;
  anomalyReasons: string[];
};

export type TokenUsageEnvironmentReport = {
  environment: string;
  totals: TokenUsageReportTotals;
  orgs: TokenUsageReportOrgRow[];
  anomalies: TokenUsageReportOrgRow[];
};

export type TokenUsageDailyReport = {
  date: string;
  generatedAt: string;
  timezone: string;
  baselineDays: number;
  environment: string;
  totals: TokenUsageReportTotals;
  environments: TokenUsageEnvironmentReport[];
  anomalies: Array<TokenUsageReportOrgRow & { environment: string }>;
  peerFetchStatus:
    | { status: 'not_requested' }
    | { status: 'success'; url: string }
    | { status: 'skipped'; reason: string }
    | { status: 'failed'; url: string; error: string };
};

export type TokenUsageFilterOptions = {
  labels: string[];
  organizations: Array<{ id: string; name: string | null }>;
  modelIds: string[];
};

export type TokenUsagePlatform = 'openai' | 'anthropic' | 'bedrock' | 'azure' | 'google';

export type TokenUsageFilters = {
  startDate?: string;
  endDate?: string;
  orgId?: string;
  label?: string;
  modelId?: string;
  platform?: TokenUsagePlatform;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

export function formatCost(cents: number): string {
  if (cents === 0) return '$0.00';
  const dollars = cents / 100;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
  return `$${dollars.toFixed(2)}`;
}

export function formatTokenCount(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Exact, comma-separated integer — for tooltips, where abbreviation (K/M/B) hides precision. */
export function formatExactNumber(n: number): string {
  return Math.round(n).toLocaleString();
}

/** Exact, comma-separated dollar amount — for tooltips, where abbreviation (K/M/B) hides precision. */
export function formatExactCost(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export { DAYS_PRESETS, getDateRange, getGranularityForRange } from '../../../hooks/useTimeRange';

// ── Query Hooks ──────────────────────────────────────────────────────────────

function buildParams(filters: TokenUsageFilters): string {
  const params = new URLSearchParams();
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.orgId) params.set('orgId', filters.orgId);
  if (filters.label) params.set('label', filters.label);
  if (filters.modelId) params.set('modelId', filters.modelId);
  if (filters.platform) params.set('platform', filters.platform);
  return params.toString();
}

async function fetchSuperadmin<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
    throw new Error(`Failed to fetch ${path}`);
  }
  return res.json();
}

export function useTokenUsageSummary(filters: TokenUsageFilters) {
  const qs = buildParams(filters);
  return useQuery({
    queryKey: ['superadmin-token-usage-summary', qs],
    queryFn: () =>
      fetchSuperadmin<{ ok: boolean; data: TokenUsageSummary; byModel: TokenUsageByModel[] }>(
        `/superadmin/token-usage/summary?${qs}`,
      ),
    staleTime: 30_000,
  });
}

export type UsageGranularity = 'hour' | '6hour' | 'day' | 'week';

export function useTokenUsageTimeSeries(
  filters: TokenUsageFilters,
  granularity: UsageGranularity = 'day',
) {
  const qs = buildParams(filters) + `&granularity=${granularity}`;
  return useQuery({
    queryKey: ['superadmin-token-usage-time-series', qs],
    queryFn: () =>
      fetchSuperadmin<{
        ok: boolean;
        data: TokenUsageTimeSeriesPoint[];
        truncated?: boolean;
        costByModel: TokenUsageCostByModelSeries;
      }>(`/superadmin/token-usage/time-series?${qs}`),
    staleTime: 30_000,
  });
}

export function useTokenUsageBreakdown(
  filters: TokenUsageFilters,
  groupBy: 'org' | 'label' | 'model' = 'org',
) {
  const qs = buildParams(filters) + `&groupBy=${groupBy}`;
  return useQuery({
    queryKey: ['superadmin-token-usage-breakdown', qs],
    queryFn: () =>
      fetchSuperadmin<{ ok: boolean; data: TokenUsageBreakdownRow[]; truncated?: boolean }>(
        `/superadmin/token-usage/breakdown?${qs}`,
      ),
    staleTime: 30_000,
  });
}

export function useTokenUsageByOrgJobType(
  filters: TokenUsageFilters,
  options: { enabled?: boolean } = {},
) {
  const qs = buildParams(filters);
  return useQuery({
    queryKey: ['superadmin-token-usage-by-org-job-type', qs],
    queryFn: () =>
      fetchSuperadmin<{ ok: boolean; data: TokenUsageJobTypeOrgRow[]; truncated?: boolean }>(
        `/superadmin/token-usage/by-org-job-type?${qs}`,
      ),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export function useTokenUsageFilterOptions() {
  return useQuery({
    queryKey: ['superadmin-token-usage-filter-options'],
    queryFn: () =>
      fetchSuperadmin<{ ok: boolean; data: TokenUsageFilterOptions }>(
        '/superadmin/token-usage/filter-options',
      ),
    staleTime: 60_000,
  });
}

export function useTokenUsageDailyReport(date?: string) {
  const qs = date
    ? `date=${encodeURIComponent(date)}&includePeer=true`
    : `date=${new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}&includePeer=true`;

  return useQuery({
    queryKey: ['superadmin-token-usage-daily-report', qs],
    queryFn: () => fetchSuperadmin<TokenUsageDailyReport>(`/superadmin/token-usage/daily-report?${qs}`),
    staleTime: 60_000,
  });
}
